import React, { FormEvent, useEffect, useState, useRef } from 'react';
import sellerService from '../services/sellerService';
import { useAppContext } from '../context/AppContext';
import countryDialCodesList from "../data/countryDialCodes.json";
interface EditSellerContactDetailsCompProps {
    show: boolean;
    onClose: () => void;
}

interface ContactDetailsFormErrors {
    email?: string;
    contactNumber?: string;
    businessName?: string;
    countryCode?: string;
}

const EditSellerContactDetailsComp: React.FC<EditSellerContactDetailsCompProps> = ({ show, onClose }) => {

    const modalRef = useRef<HTMLDivElement | null>(null);
    const bsModalRef = useRef<any>(null);

    const { seller, setSeller } = useAppContext();

    const [email, setEmail] = useState(seller!.email);
    const [contactNumber, setContactNumber] = useState(seller!.contactNumber);
    const [businessName, setBusinessName] = useState(seller!.businessName);
    const [countryCode, setCountryCode] = useState(seller!.countryCode || "");

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [contactDetailsFormErrors, setContactDetailsFormErrors] = useState<ContactDetailsFormErrors>({});
    const [serviceStatus, setServiceStatus] = useState({ type: '', message: '' });
    const [countryDialCodes] = useState(countryDialCodesList); // Assume countryDialCodesList is imported or defined elsewhere

    useEffect(() => {
        if (modalRef.current) {
            bsModalRef.current = new (window as any).bootstrap.Modal(modalRef.current);
            modalRef.current.addEventListener('hidden.bs.modal', onClose);
        }
        return () => {
            modalRef.current?.removeEventListener('hidden.bs.modal', onClose);
        };
    }, []);

    useEffect(() => {
        if (show) {
            setEmail(seller!.email);
            setContactNumber(seller!.contactNumber);
            setBusinessName(seller!.businessName);
            setCountryCode(seller!.countryCode || "");
            bsModalRef.current?.show();
        } else {
            bsModalRef.current?.hide();
        }
    }, [show]);

    const validateContactDetails = () => {
        const errors: ContactDetailsFormErrors = {};
        const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        const phonePattern = /^[\d\s\-()]{5,15}$/;
        if (!businessName) {
            errors.businessName = "Business Name is required";
        }
        else if (businessName.length > 100) {
            errors.businessName = "Business Name must be less than 100 characters";
        }

        if (!email) {
            errors.email = "Email is required";
        } else if (!emailPattern.test(email)) {
            errors.email = "Email address is invalid";
        }

        if (!countryCode) {
            errors.countryCode = "Country code is required";
        }

        if (!contactNumber) {
            errors.contactNumber = "Phone number is required";
        } else if (!phonePattern.test(contactNumber)) {
            errors.contactNumber = "Phone number is invalid";
        }
        setContactDetailsFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleContactDetailSubmit = (e: FormEvent) => {
        const updateContactDetails = async () => {
            try {
                const updateSucceed = await sellerService.updateSellerBusinessAndContactDetails(businessName, email, contactNumber, countryCode);
                if (updateSucceed === "Success") {
                    setSeller({ ...seller!, businessName: businessName, email: email, contactNumber: contactNumber, countryCode: countryCode });
                    onClose();
                } else {
                    setServiceStatus({ type: "Error", message: "Failed to update the contact details." });
                }
            } catch (error) {
                setServiceStatus({ type: "Error", message: "Failed to update the contact details." });
            }
            setIsSubmitting(false);
        };
        e.preventDefault();
        setServiceStatus({ type: "", message: "" });
        if (validateContactDetails()) {
            setIsSubmitting(true);
            updateContactDetails();
        }
    };

    return (
        <div className="modal fade" tabIndex={-1} ref={modalRef} data-bs-backdrop="static" data-bs-keyboard="false" >
            <div className="modal-dialog">
                <div className="modal-content rounded-0">
                    <div className="modal-header">
                        <h5 className="modal-title">Edit Business &amp; Contact Details</h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => bsModalRef.current?.hide()}
                        ></button>
                    </div>
                    <div className="modal-body">
                        <div className='row g-3'>
                            <div className="col-md-12">
                                <label htmlFor="inputBusinessName" className="form-label">Business Name</label>
                                <input
                                    type="text"
                                    className={`form-control ${contactDetailsFormErrors.businessName ? 'is-invalid' : ''}`}
                                    id="inputBusinessName"
                                    value={businessName}
                                    onChange={(e) => setBusinessName(e.target.value)}
                                />
                                {contactDetailsFormErrors.businessName && <div className="invalid-feedback">{contactDetailsFormErrors.businessName}</div>}
                            </div>
                            <div className="col-md-12">
                                <label htmlFor="inputEmail" className="form-label">Email</label>
                                <input
                                    type="email"
                                    className={`form-control ${contactDetailsFormErrors.email ? 'is-invalid' : ''}`}
                                    id="inputEmail"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                                {contactDetailsFormErrors.email && <div className="invalid-feedback">{contactDetailsFormErrors.email}</div>}
                            </div>
                            <div className='d-flex align-items-center  mb-1'>
                                <div className="col-md-3 me-2">
                                    <label htmlFor="inputCountryCode" className="form-label">Country Code</label>
                                    <div className="dropdown">
                                        <button
                                            className="btn btn-outline-dark dropdown-toggle  w-100 "
                                            type="button"
                                            data-bs-toggle="dropdown"
                                        >
                                            {countryCode || "Select"}
                                        </button>
                                        <ul className="dropdown-menu" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                            {countryDialCodes.map(c => (
                                                <li key={c.code}>
                                                    <button
                                                        className="dropdown-item"
                                                        type="button"
                                                        onClick={() => setCountryCode("+" + c.code)}
                                                    >
                                                        {c.country} +{c.code}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    {contactDetailsFormErrors.countryCode && <div className="invalid-feedback">{contactDetailsFormErrors.countryCode}</div>}
                                </div>
                                <div className="w-100">
                                    <label htmlFor="inputPhone" className="form-label">Phone</label>
                                    <input
                                        type="text"
                                        className={`form-control ${contactDetailsFormErrors.contactNumber ? 'is-invalid' : ''}`}
                                        id="inputPhone"
                                        value={contactNumber}
                                        onChange={(e) => setContactNumber(e.target.value)}
                                    />
                                    {contactDetailsFormErrors.contactNumber && <div className="invalid-feedback">{contactDetailsFormErrors.contactNumber}</div>}
                                </div>
                            </div>

                            {serviceStatus.type === "Error" && <div className="col-md-12"><div className="col-md-12 clearfix"><div className="alert alert-danger p-2 mb-0">{serviceStatus.message}</div></div></div>}
                        </div>
                    </div>
                    <div className="modal-footer d-flex justify-content-between">
                        <button className="btn btn-outline-dark rounded-0" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-dark rounded-0" onClick={handleContactDetailSubmit} disabled={isSubmitting}>
                            {isSubmitting ? (<><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Saving ...</>) : ("Save")}
                        </button>
                    </div>
                </div>
            </div >
        </div >
    );

};

export default EditSellerContactDetailsComp;
